// Horowitzova městská banka currency: zlatka / stříbrný / měďák / groš.
// Base-10 at every level. Used uniformly across the app for every amount of
// money, including a character's real held cash (acore_characters.characters
// .money) - this server's economy runs on these four coins, not vanilla
// WoW's gold/silver/copper, so there is only ever this one formatter.
// All conversion math (zlatka/stříbrný/měďák input -> groš) happens on the
// backend (economy/currency.py); this file only formats already-in-groš
// amounts the API returns, so a plain JS number is safe here.

export const GROS_PER_MEDAK = 10;
export const GROS_PER_STRIBRNY = 100;
export const GROS_PER_ZLATKA = 1000;

export function splitGros(amount) {
  const n = Number(amount) || 0;
  const whole = Math.trunc(n);
  const zlatka = Math.trunc(whole / GROS_PER_ZLATKA);
  const stribrny = Math.trunc((whole % GROS_PER_ZLATKA) / GROS_PER_STRIBRNY);
  const medak = Math.trunc((whole % GROS_PER_STRIBRNY) / GROS_PER_MEDAK);
  const gros = whole % GROS_PER_MEDAK;
  return { zlatka, stribrny, medak, gros, fraction: n - whole };
}

// "12 Zl 3 St 4 Mď 5 Gr" - always shows all four coins, even when zero, so
// the reader always sees the full breakdown across every denomination.
export function formatGros(amount, { showFraction = false } = {}) {
  const { zlatka, stribrny, medak, gros, fraction } = splitGros(amount);
  const parts = [`${zlatka} Zl`, `${stribrny} St`, `${medak} Mď`, `${gros} Gr`];
  if (showFraction && fraction > 0) {
    parts[parts.length - 1] += ` (+${fraction.toFixed(2)} nevyplaceno)`;
  }
  return parts.join(" ");
}

// GM-facing forms take a single decimal "zlatka" input (e.g. "350.5") rather
// than four separate zlatka/stříbrný/měďák/groš fields - this splits it into
// the MoneyInput shape the API expects. The backend re-derives the canonical
// groš total server-side (economy/currency.py); this is purely for shaping
// the request payload.
export function parseZlatkaInput(value) {
  const num = Number(value);
  if (!Number.isFinite(num) || num < 0) return null;
  const totalGros = Math.round(num * GROS_PER_ZLATKA);
  const zlatka = Math.trunc(totalGros / GROS_PER_ZLATKA);
  const remainder = totalGros % GROS_PER_ZLATKA;
  const stribrny = Math.trunc(remainder / GROS_PER_STRIBRNY);
  const medak = Math.trunc((remainder % GROS_PER_STRIBRNY) / GROS_PER_MEDAK);
  const gros = remainder % GROS_PER_MEDAK;
  return { zlatka, stribrny, medak, gros };
}

// Inverse of parseZlatkaInput, for prefilling an edit form from a stored groš amount.
export function grosToZlatkaDecimalString(amountGros) {
  const n = Number(amountGros) || 0;
  return (n / GROS_PER_ZLATKA).toString();
}

// Compact "350 Zl" (whole zlatky only, no sub-denominations) - for chart axis
// ticks where the full four-coin breakdown would be too cramped; the exact
// breakdown still belongs in tooltips via formatGros().
export function formatZlatkaCompact(amountGros) {
  const zlatka = Math.round((Number(amountGros) || 0) / GROS_PER_ZLATKA);
  return `${zlatka} Zl`;
}
